<script setup lang="ts">
const team = [
  {
    name: 'Andreas Szklarek',
    role: 'Architekt, Inhaber',
    image: '/images/team/andreas_szklarek.jpg'
  },
  {
    name: 'Elena Rollheiser',
    role: 'Architektin',
    image: '/images/team/elena_rollheiser.jpg'
  },
  {
    name: 'Shirin Safari',
    role: 'Architektin',
    image: '/images/team/shirin_safari.jpg'
  },
  {
    name: 'Martina Stelitano',
    role: 'Architektin',
    image: '/images/team/martina_stelitano.jpg'
  },
  {
    name: 'Nina Peters',
    role: 'Assistenz der Geschäftsführung',
    image: '/images/team/nina_peters.jpg'
  }
]

const getTriangleDirection = (index: number) => {
  return index % 2 === 0 ? 'triangle-shape-right' : 'triangle-shape-left'
}
</script>

<template>
  <div class="px-0 py-0 mx-0 my-0">
    <div class="pt-high">
      <v-row class="fill-height" no-gutters>
        <template v-for="(member, index) in team" :key="member.name">
          <!-- Image Column -->
          <v-col cols="6" lg="3" :class="`image-col-${index}`">
            <div class="shape-container">
              <v-img
                class="zoom-on-hover"
                :src="member.image"
                :alt="member.name"
                cover
                aspect-ratio="1"
              />
              <div
                :class="[getTriangleDirection(index), `triangle-lg-${index}`]"
                aria-hidden="true"
              ></div>
            </div>
          </v-col>

          <!-- Text Column -->
          <v-col cols="6" lg="3" :class="`px-6 py-6 px-lg-8 py-lg-8 text-col-${index}`">
            <div class="d-flex flex-column h-100">
              <div class="d-flex align-start justify-start">
                <p class="text-primary team-name font-weight-regular">
                  {{ member.name.split(' ')[0] }} <br />
                  {{ member.name.split(' ')[1] }}
                </p>
              </div>
              <div class="d-flex justify-start mt-auto">
                <p class="text-h6 text-lg-h3 font-weight-regular text-grey">{{ member.role }}</p>
              </div>
            </div>
          </v-col>
        </template>
      </v-row>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Small screen orders
.image-col-0 {
  order: 0;
}
.text-col-0 {
  order: 1;
}
.image-col-1 {
  order: 3;
}
.text-col-1 {
  order: 2;
}
.image-col-2 {
  order: 4;
}
.text-col-2 {
  order: 5;
}
.image-col-3 {
  order: 7;
}
.text-col-3 {
  order: 6;
}
.image-col-4 {
  order: 8;
}
.text-col-4 {
  order: 9;
}
.image-col-5 {
  order: 11;
}
.text-col-5 {
  order: 10;
}
.image-col-6 {
  order: 12;
}
.text-col-6 {
  order: 13;
}

// Large screen orders
@media (min-width: 1280px) {
  .image-col-0 {
    order: 0;
  }
  .text-col-0 {
    order: 1;
  }
  .image-col-1 {
    order: 2;
  }
  .text-col-1 {
    order: 3;
  }
  .image-col-2 {
    order: 5;
  }
  .text-col-2 {
    order: 4;
  }
  .image-col-3 {
    order: 7;
  }
  .text-col-3 {
    order: 6;
  }
  .image-col-4 {
    order: 8;
  }
  .text-col-4 {
    order: 9;
  }
  .image-col-5 {
    order: 10;
  }
  .text-col-5 {
    order: 11;
  }
  .image-col-6 {
    order: 13;
  }
  .text-col-6 {
    order: 12;
  }

  // Triangle directions for large screens
  // Row 0 (0,1): right, right
  .triangle-lg-0 {
    border-left: 2.5vw solid transparent !important;
    border-right: 2.5vw solid #fff !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    right: 0 !important;
    left: auto !important;
  }
  .triangle-lg-1 {
    border-left: 2.5vw solid transparent !important;
    border-right: 2.5vw solid #fff !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    right: 0 !important;
    left: auto !important;
  }

  // Row 1 (2,3): left, left
  .triangle-lg-2 {
    border-left: 2.5vw solid #fff !important;
    border-right: 2.5vw solid transparent !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    left: 0 !important;
    right: auto !important;
  }
  .triangle-lg-3 {
    border-left: 2.5vw solid #fff !important;
    border-right: 2.5vw solid transparent !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    left: 0 !important;
    right: auto !important;
  }

  // Row 2 (4,5): right, right
  .triangle-lg-4 {
    border-left: 2.5vw solid transparent !important;
    border-right: 2.5vw solid #fff !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    right: 0 !important;
    left: auto !important;
  }
  .triangle-lg-5 {
    border-left: 2.5vw solid transparent !important;
    border-right: 2.5vw solid #fff !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    right: 0 !important;
    left: auto !important;
  }

  // Row 3 (6): left
  .triangle-lg-6 {
    border-left: 2.5vw solid #fff !important;
    border-right: 2.5vw solid transparent !important;
    border-bottom: 2.5vw solid transparent !important;
    border-top: 2.5vw solid transparent !important;
    left: 0 !important;
    right: auto !important;
  }
}

.zoom-on-hover {
  transition: transform 0.5s;
}

.v-img.zoom-on-hover:hover {
  transform: scale(1.1);
}

.v-img.zoom-on-hover:focus-within {
  transform: scale(1.1);
}

.shape-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &:hover {
    img {
      transform: scale(1.1);
    }
  }

  &:focus-within {
    img {
      transform: scale(1.1);
    }
  }
}

.triangle-shape-right {
  border-left: 2.5vw solid transparent;
  border-right: 2.5vw solid #fff;
  border-bottom: 2.5vw solid transparent;
  border-top: 2.5vw solid transparent;
  display: inline-block;
  position: absolute;
  top: 50%;
  right: 0;
  z-index: 1;
  transform: translate(0, -50%);
}

.triangle-shape-left {
  border-left: 2.5vw solid #fff;
  border-right: 2.5vw solid transparent;
  border-bottom: 2.5vw solid transparent;
  border-top: 2.5vw solid transparent;
  display: inline-block;
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 1;
  transform: translate(0, -50%);
}

@media (min-width: 1280px) {
  .triangle-lg-shape-right {
    border-left: 2.5vw solid transparent;
    border-right: 2.5vw solid #fff;
    border-bottom: 2.5vw solid transparent;
    border-top: 2.5vw solid transparent;
    display: inline-block;
    position: absolute;
    top: 50%;
    right: 0;
    z-index: 1;
    transform: translate(0, -50%);
  }

  .triangle-lg-shape-left {
    border-left: 2.5vw solid #fff;
    border-right: 2.5vw solid transparent;
    border-bottom: 2.5vw solid transparent;
    border-top: 2.5vw solid transparent;
    display: inline-block;
    position: absolute;
    top: 50%;
    left: 0;
    z-index: 1;
    transform: translate(0, -50%);
  }
}
</style>
